import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface PatientCategory {
  id: string;
  name: string;
  label: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface CreatePatientCategoryDto {
  name: string;
  label: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdatePatientCategoryDto {
  name?: string;
  label?: string;
  description?: string;
  isActive?: boolean;
}

export type PatientCategoryStatusFilter = 'active' | 'inactive';

export interface PatientCategoryListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: PatientCategoryStatusFilter;
}

export type PatientCategoryListResponse = ApiResponse<PatientCategory[]> & {
  meta?: PaginationMeta;
  stats?: { trashed?: number };
};

export interface PatientCategoryTrashResult {
  restored?: string[];
  deleted?: string[];
  notFound: string[];
  blocked?: { id: string; reason: string }[];
}

function buildListQuery(params: PatientCategoryListParams = {}): string {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.status) query.set('status', params.status);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export const patientCategoryService = {
  /** Paginated, searchable, filterable management list (includes the trash count). */
  getAll: (params: PatientCategoryListParams = {}): Promise<PatientCategoryListResponse> => {
    return get<PatientCategory[]>(`/patient-categories${buildListQuery(params)}`) as Promise<PatientCategoryListResponse>;
  },

  /** Every active category, used by the patient form dropdown. */
  getActive: (): Promise<ApiResponse<PatientCategory[]>> => {
    return get<PatientCategory[]>('/patient-categories', { active: 'true' });
  },

  /** List soft-deleted categories (the Trash page). */
  getTrashed: (page = 1, limit = 20, search?: string): Promise<ApiResponse<PatientCategory[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<PatientCategory[]>(`/patient-categories/trash?${params.toString()}`) as Promise<
      ApiResponse<PatientCategory[]> & { meta: PaginationMeta }
    >;
  },

  getOne: (id: string): Promise<ApiResponse<PatientCategory>> => {
    return get<PatientCategory>(`/patient-categories/${id}`);
  },

  create: (data: CreatePatientCategoryDto): Promise<ApiResponse<PatientCategory>> => {
    return post<PatientCategory>('/patient-categories', data);
  },

  update: (id: string, data: UpdatePatientCategoryDto): Promise<ApiResponse<PatientCategory>> => {
    return patch<PatientCategory>(`/patient-categories/${id}`, data);
  },

  /** Soft delete — moves the category to the Trash. */
  remove: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/patient-categories/${id}`);
  },

  /** Restore trashed categories back to the active list. */
  restoreCategories: (ids: string[]): Promise<ApiResponse<PatientCategoryTrashResult>> => {
    return post<PatientCategoryTrashResult>('/patient-categories/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed categories. */
  permanentlyDeleteCategories: (ids: string[]): Promise<ApiResponse<PatientCategoryTrashResult>> => {
    return post<PatientCategoryTrashResult>('/patient-categories/trash/bulk-delete', { ids });
  },
};
